import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import {
  useFloating,
  useInteractions,
  useMergeRefs,
  FloatingFocusManager,
  FloatingPortal,
  useDismiss,
  useTransitionStatus,
} from '@floating-ui/react';

import { ToastCloseType } from '../types';
import { useDragDismiss } from '../hooks/useDragDismiss';
import { TOAST_DOM_ID } from '../../constants';
import { RCCloseButton, RCIcon, RCThemeOverride } from '../';
import { HeartStraightIcon, CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';

export type ToastThemes = 'plain' | 'watch-add' | 'watch-remove' | 'success' | 'error';

interface RCToastProps {
  /**
   * Controls if the Toast should display.
   *
   * Keep in mind that Toast will self-close after 10 seconds unless the user is hovering over it.
   * */
  isOpen: boolean,

  /**
   * This is auto-fired after a short interval unless the user is hovering over it.
  */
  onClose: (closeType: ToastCloseType) => void,

  /**
   * The toast message. Keep it short (1-4 words) to minimize text wrapping on mobile, especially for non-English languages.
  */
  text: string,

  /**
   * Our themes dictate the color & icon of a given toast;
   * if you need a new theme, the Cadence team can help (or simply sign off on the new theme you make).
  */
  theme?: ToastThemes,

  /** A CoreLink element without any styles. Default link text should use 'commons.RCToast.linkDefault' ("View").
   * Custom text should be very short (1-2 words max) for the best UI/UX balance.
   */
  link?: React.ReactNode;

  /** Value in rems greater than 1. Adjust toast placement to avoid covering important UI elements. */
  mobileOffset?: number,

  /** How long should the toast stay on the screen? This value only applies to toasts _without_ a `link` present.
   * If a `link` is present, we have a fixed delay to help users "find" that link in a consistent amount of time.
   * Short = 2.5 seconds, medium = 5 seconds (default), long = 10 seconds (default when link is present)
   */
  delayInterval?: 'short' | 'medium' | 'long';
}

const DELAY_TIME_MS = {
  short: 2000,
  medium: 5000,
  long: 10000,
};

const EASE_IN_OUT: [number, number, number, number] = [0.42, 0, 0.58, 1];

// a swipe down past this many px flies it out the rest of the way it went, fading
const SWIPE_DISTANCE = 30;
const SWIPE_FLY_OUT = {
  to: (y: number) => y * 2,
  duration: 200,
  ease: EASE_IN_OUT,
};

export function RCToast({
  text,
  isOpen,
  onClose,
  theme = 'plain',
  link = undefined,
  mobileOffset = undefined,
  delayInterval = 'medium',
}: RCToastProps) {
  const [hasAttention, setHasAttention] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const delayDuration = link ? DELAY_TIME_MS.long : DELAY_TIME_MS[delayInterval];
  const BUFFERED_CLOSE_DELAY = 1000;
  const id = React.useId();
  const open = isOpen; // Needed to avoid Window open() function collision
  const {
    context,
    refs,
  } = useFloating({
    open,
    onOpenChange: handleDismiss,
  });

  const { getFloatingProps } = useInteractions([
    useDismiss(context, {
      enabled: true,
      escapeKey: true,
      outsidePress: false,
    }),
  ]);

  const { isMounted, status } = useTransitionStatus(context);
  const toastRef = useRef<HTMLDivElement>(null);
  const ref = useMergeRefs([refs.setFloating, toastRef]);
  const drag = useDragDismiss({
    ref: toastRef,
    upElastic: 0.1,
    dismissDistance: SWIPE_DISTANCE,
    flyOut: SWIPE_FLY_OUT,
    onDismiss: async (flownOut) => {
      await flownOut;
      cleanupAndClose('swipe');
    },
  });

  useEffect(() => {
    let timer;

    if (!isOpen) return;

    if (hasAttention) {
      clearTimeout(timer);
    }

    if (!hasInteracted) {
      timer = setTimeout(() => handleTimeout(), delayDuration);
    }

    if (hasInteracted) {
      timer = setTimeout(() => handleTimeout(), BUFFERED_CLOSE_DELAY);
    }

    return () => clearTimeout(timer);
  }, [isOpen, hasAttention, hasInteracted]);

  async function handleTimeout() {
    const transitionDistance = mobileOffset ? mobileOffset + 10 : 10;
    if (hasAttention) return;
    setHasInteracted(false);
    // Animate out - slide down and off screen
    await animateOut([{ transform: `translateY(${transitionDistance}rem)` }], 300);
    cleanupAndClose('timeout');
  }

  function cleanupAndClose(closeType: ToastCloseType) {
    setHasAttention(false);
    setHasInteracted(false);
    onClose(closeType);
  }

  async function handleDismiss() {
    // Animate out - fade out in place with a small scale effect
    await animateOut([{ opacity: 0, transform: 'scale(0.9)' }], 200);
    cleanupAndClose('dismiss');
  }

  // Resolves once the toast has animated out, or right away where it can't animate.
  async function animateOut(keyframes: Keyframe[], duration: number) {
    const toast = toastRef.current;
    if (!toast?.animate) return;

    const animation = toast.animate(keyframes, {
      duration,
      easing: `cubic-bezier(${EASE_IN_OUT.join(', ')})`,
      fill: 'forwards',
    });
    await animation.finished.catch(() => undefined);
  }

  function onAttentionLeave() {
    setHasAttention(false);
  }

  function onHasAttention() {
    if (hasAttention) return;
    setHasAttention(true);
    setHasInteracted(true);
  }

  function isDialog() {
    return !!link;
  }

  const themeIcon = {
    'plain': null,
    'watch-add': <HeartStraightIcon weight="fill" />,
    'watch-remove': <HeartStraightIcon />,
    'success': <CheckCircleIcon />,
    'error': <WarningCircleIcon />,
  };

  function toastIcon() {
    const icon = themeIcon[theme];
    if (!icon) return null;

    const iconClasses = classNames(
      'rc-toast__icon', {
        'rc-toast__icon--watch-add': theme === 'watch-add',
        'rc-toast__icon--watch-remove': theme === 'watch-remove',
        'rc-toast__icon--success': theme === 'success',
        'rc-toast__icon--error': theme === 'error',
      },
    );

    return (
      <div className={iconClasses}>
        <RCIcon icon={icon} size="6" />
      </div>
    );
  }

  const mobileOffsetStyle = { '--mobile-offset': `${mobileOffset}rem` } as React.CSSProperties;

  function innerContents() {
    return (
      <div
        className="rc-toast-wrapper"
        style={mobileOffset ? mobileOffsetStyle : {}}
        data-status={status} // used for entry animation handling
      >
        <RCThemeOverride theme="dark">
          {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
          <div
            ref={ref}
            key="toast"
            className="rc-toast"
            onPointerDown={drag.start}
            onMouseOver={() => onHasAttention()}
            onMouseLeave={() => onAttentionLeave()}
            role={isDialog() ? 'dialog' : 'status'}
            {...getFloatingProps()}
          >
            <div className="rc-toast__content">
              {toastIcon()}
              <div className="rc-toast__text" id={id}>
                {text}
              </div>
            </div>
            {isDialog() && link}
            <div className="rc-toast__close-button">
              <RCCloseButton
                onClick={() => handleDismiss()}
                aria-label={I18n.t('cadence.RCToast.closeDialog')}
              />
            </div>
          </div>
        </RCThemeOverride>
      </div>
    );
  }

  if (!isMounted) return null;

  return (
    <FloatingPortal id={TOAST_DOM_ID}>
      {isDialog() ? (
        <FloatingFocusManager
          context={context}
          order={['content']}
          returnFocus={false} // Fixes repositioning scroll bug after dismissal: https://reverb.atlassian.net/browse/HF-752
          modal
        >
          {innerContents()}
        </FloatingFocusManager>
      ) : (
        <>
          {innerContents()}
        </>
      )}
    </FloatingPortal>
  );
}
